import React, { useState, useEffect } from 'react';
import AnalyticsExport from './AnalyticsExport';
import './AnalyticsDashboard.css';

interface ClassPracticeStats {
  projectId: string;
  totalStudents: number;
  studentsWhoPracticed: number;
  practiceCompletionRate: number;
  averagePracticeTime: number;
  totalPracticeSessions: number;
  sceneStats: {
    sceneId: string;
    sceneTitle: string;
    studentsPracticed: number;
    completionRate: number;
  }[];
}

interface StudentPracticeStatus {
  userId: string;
  userName: string;
  sceneId: string;
  sceneTitle: string;
  hasPracticed: boolean;
  lastPracticeDate: Date | null;
  practiceCount: number;
  status: 'practiced' | 'not_practiced' | 'needs_attention';
}

interface CueConflictAnalysis {
  sceneId: string;
  sceneTitle: string;
  cueId: string;
  cueName: string;
  conflictCount: number;
  affectedStudents: number;
  averageOffset: number;
  severity: 'low' | 'medium' | 'high';
}

interface AnalyticsDashboardProps {
  projectId: string;
  teacherId: string;
  onClose?: () => void;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ projectId, teacherId, onClose }) => {
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  const [classStats, setClassStats] = useState<ClassPracticeStats | null>(null);
  const [studentStatus, setStudentStatus] = useState<StudentPracticeStatus[]>([]);
  const [cueConflicts, setCueConflicts] = useState<CueConflictAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedScene, setSelectedScene] = useState<string | null>(null);
  const [inactiveDays, setInactiveDays] = useState(7);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [projectId, selectedScene, inactiveDays]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const statsResponse = await fetch(`${apiBase}/analytics/class-practice-stats/${projectId}`);
      if (!statsResponse.ok) throw new Error('Failed to load class statistics');
      const stats = await statsResponse.json();
      setClassStats(stats);

      // Load student practice status
      const statusUrl = selectedScene
        ? `${apiBase}/analytics/student-practice-status/${projectId}?sceneId=${selectedScene}&inactiveDays=${inactiveDays}`
        : `${apiBase}/analytics/student-practice-status/${projectId}?inactiveDays=${inactiveDays}`;
      const statusResponse = await fetch(statusUrl);
      if (!statusResponse.ok) throw new Error('Failed to load student status');
      const status = await statusResponse.json();
      setStudentStatus(status);

      // Load cue conflicts
      const conflictsUrl = selectedScene
        ? `${apiBase}/analytics/cue-conflicts/${projectId}?sceneId=${selectedScene}`
        : `${apiBase}/analytics/cue-conflicts/${projectId}`;
      const conflictsResponse = await fetch(conflictsUrl);
      if (!conflictsResponse.ok) throw new Error('Failed to load cue conflicts');
      const conflicts = await conflictsResponse.json();
      setCueConflicts(conflicts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString();
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'practiced': return '✓';
      case 'needs_attention': return '⚠';
      case 'not_practiced': return '✗';
      default: return '?';
    }
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'practiced': return 'status-practiced';
      case 'needs_attention': return 'status-needs-attention';
      case 'not_practiced': return 'status-not-practiced';
      default: return '';
    }
  };

  const getSeverityClass = (severity: string): string => {
    switch (severity) {
      case 'high': return 'severity-high';
      case 'medium': return 'severity-medium';
      case 'low': return 'severity-low';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-dashboard">
        <div className="error">{error}</div>
        <button onClick={loadAnalytics}>Retry</button>
      </div>
    );
  }

  if (!classStats) {
    return (
      <div className="analytics-dashboard">
        <div className="error">No analytics data available</div>
      </div>
    );
  }

  // Group student status by scene for matrix view
  const sceneMap = new Map<string, StudentPracticeStatus[]>();
  studentStatus.forEach(status => {
    if (!sceneMap.has(status.sceneId)) {
      sceneMap.set(status.sceneId, []);
    }
    sceneMap.get(status.sceneId)!.push(status);
  });

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <h2>Class Analytics</h2>
        <div className="header-actions">
          <button className="export-button" onClick={() => setShowExport(!showExport)}>
            {showExport ? 'Hide Export' : 'Export Data'}
          </button>
          {onClose && (
            <button className="close-button" onClick={onClose}>×</button>
          )}
        </div>
      </div>

      {showExport && (
        <div className="export-panel">
          <AnalyticsExport 
            projectId={projectId} 
            teacherId={teacherId}
            onClose={() => setShowExport(false)} 
          />
        </div>
      )}

      {/* Class-wide overview */}
      <section className="overview-section">
        <h3>Class Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{classStats.totalStudents}</div>
            <div className="stat-label">Total Students</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{classStats.studentsWhoPracticed}</div>
            <div className="stat-label">Students Practiced</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{classStats.practiceCompletionRate.toFixed(1)}%</div>
            <div className="stat-label">Completion Rate</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatTime(classStats.averagePracticeTime)}</div>
            <div className="stat-label">Avg Practice Time</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{classStats.totalPracticeSessions}</div>
            <div className="stat-label">Total Sessions</div>
          </div>
        </div>
      </section>

      {/* Scene-by-scene completion */}
      <section className="scene-stats-section">
        <h3>Scene Completion Rates</h3>
        <div className="scene-stats-list">
          {classStats.sceneStats.map(scene => (
            <div key={scene.sceneId} className="scene-stat-item">
              <div className="scene-info">
                <span className="scene-title">{scene.sceneTitle}</span>
                <span className="scene-count">{scene.studentsPracticed} / {classStats.totalStudents} students</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${scene.completionRate}%` }}
                />
              </div>
              <span className="completion-rate">{scene.completionRate.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="filters-section">
        <div className="filter-group">
          <label htmlFor="scene-filter">Filter by Scene:</label>
          <select 
            id="scene-filter"
            value={selectedScene || ''} 
            onChange={(e) => setSelectedScene(e.target.value || null)}
          >
            <option value="">All Scenes</option>
            {classStats.sceneStats.map(scene => (
              <option key={scene.sceneId} value={scene.sceneId}>
                {scene.sceneTitle}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="inactive-days">Inactive Threshold:</label>
          <select 
            id="inactive-days"
            value={inactiveDays} 
            onChange={(e) => setInactiveDays(parseInt(e.target.value))}
          >
            <option value="3">3 days</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
          </select>
        </div>
      </section>

      {/* Student practice status matrix */}
      <section className="student-status-section">
        <h3>Student Practice Status</h3>
        <div className="status-legend">
          <span className="legend-item">
            <span className="status-icon status-practiced">✓</span> Practiced
          </span>
          <span className="legend-item">
            <span className="status-icon status-needs-attention">⚠</span> Needs Attention
          </span>
          <span className="legend-item">
            <span className="status-icon status-not-practiced">✗</span> Not Practiced
          </span>
        </div>
        
        {selectedScene ? (
          // Single scene view with details
          <div className="student-list">
            {studentStatus.map(status => (
              <div key={`${status.userId}-${status.sceneId}`} className={`student-item ${getStatusClass(status.status)}`}>
                <span className="status-icon">{getStatusIcon(status.status)}</span>
                <span className="student-name">{status.userName}</span>
                <span className="practice-count">{status.practiceCount} sessions</span>
                <span className="last-practice">{formatDate(status.lastPracticeDate)}</span>
              </div>
            ))}
          </div>
        ) : (
          // Matrix view for all scenes
          <div className="practice-matrix">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  {classStats.sceneStats.map(scene => (
                    <th key={scene.sceneId}>{scene.sceneTitle}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from(new Set(studentStatus.map(s => s.userId))).map(userId => {
                  const studentName = studentStatus.find(s => s.userId === userId)?.userName || 'Unknown';
                  return (
                    <tr key={userId}>
                      <td className="student-name-cell">{studentName}</td>
                      {classStats.sceneStats.map(scene => {
                        const status = studentStatus.find(s => s.userId === userId && s.sceneId === scene.sceneId);
                        return (
                          <td key={scene.sceneId} className={`status-cell ${status ? getStatusClass(status.status) : ''}`}>
                            {status ? getStatusIcon(status.status) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Cue conflicts */}
      {cueConflicts.length > 0 && (
        <section className="cue-conflicts-section">
          <h3>Common Cue Conflicts</h3>
          <div className="conflicts-list">
            {cueConflicts.map(conflict => (
              <div key={conflict.cueId} className={`conflict-item ${getSeverityClass(conflict.severity)}`}>
                <div className="conflict-header">
                  <span className="cue-name">{conflict.cueName}</span>
                  <span className={`severity-badge ${getSeverityClass(conflict.severity)}`}>
                    {conflict.severity}
                  </span>
                </div>
                <div className="conflict-details">
                  <span className="scene-title">{conflict.sceneTitle}</span>
                  <span className="conflict-stats">
                    {conflict.conflictCount} conflicts • {conflict.affectedStudents} students • 
                    Avg offset: {conflict.averageOffset}ms
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
